export interface BoardGame {
  id: string;
  name: string;
  year: number;
  description: string;
  imageUrl: string;
  category: string[];
  minPlayers: number;
  maxPlayers: number;
  playTime: number;
  complexity: number;
  owned: boolean;
  wishlist: boolean;
  bggId?: number;
}

export interface Game {
  id: string;
  name: string;
  year: number | null;
  ano_nacional?: number | null;
  description: string | null;
  image_url: string | null;
  thumb_url: string | null;
  link?: string | null;
  categories: string[] | null;
  min_players: number | null;
  max_players: number | null;
  play_time: number | null;
  min_age?: number | null;
  complexity: number | null;
  average_rating?: number | null;
  average_complexity?: number | null;
  bgg_id: number | null;
  ludopedia_id?: string | null;
  created_at: string;
  updated_at: string;
  mechanics: string[] | null;
  themes: string[] | null;
  artists: string[] | null;
  designers: string[] | null;
  store_products?: StoreProduct[];
  stats?: GameStats | null;
  slug: string | null;
}

export interface GameStats {
  owned_count?: number;
  wishlist_count?: number;
  rating_count?: number;
  play_count?: number;
  played_count?: number;
  average_rating?: number | null;
  average_complexity?: number | null;
}

export interface UserGame {
  id: string;
  user_id: string;
  game_id: string;
  owned: boolean | null;
  wishlist: boolean | null;
  rating: number | null;
  complexity_rating?: number | null;
  play_count: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Join com a tabela games para exibição
  game?: Game;
  game_slug?: string;
}

export interface UserGameWithDetails extends UserGame {
  game: Game;
}

// Critérios para buscar jogos de um usuário
export interface UserGamesCriteria {
  minRating?: number;
  owned?: boolean; // Exemplo para futuras expansões
  orderBy?: {
    field: string;
    ascending: boolean;
  };
}

export interface Profile {
  id: string;
  name: string | null;
  avatarUrl: string | null;
  favoriteCategories: string[] | null;
  preferredPlayerCount: number | null;
  maxPlayTime: number | null;
  complexityPreference: number | null;
}

export interface UserProfile {
  id: string;
  email: string;
  username?: string;
  full_name?: string;
  avatar_url?: string;
  bio?: string;
  favorite_categories?: string[];
  is_admin?: boolean;
  created_at: string;
  updated_at: string;
  ludopedia_user_id?: string;
  ludopedia_username?: string;
  ludopedia_access_token?: string;
  terms_accepted_at?: string;
  marketing_consent?: boolean;
  referral_code?: string;
  referred_by_id?: string;
}

export interface Store {
  id: string;
  name: string;
  url: string;
  logo_url: string | null;
  affiliate_code: string | null;
  created_at: string;
  updated_at: string;
}

export interface StoreProduct {
  id: string;
  store_id: string;
  game_id: string;
  product_url: string;
  price: number | null;
  available: boolean | null;
  last_checked: string;
  created_at: string;
  updated_at: string;
  // Join com a tabela stores para exibição
  store?: Store;
  // Join com a tabela games para exibição
  game?: Game;
}

export interface PriceHistory {
  id: string;
  store_product_id: string;
  price: number;
  recorded_at: string;
  // Join opcional com store_product e store para exibição
  store_product?: StoreProduct;
  store?: Store;
}

export interface Recommendation {
  id: string;
  user_id: string;
  game_id: string;
  score: number;
  reason?: string;
  algorithm?: RecommendationAlgorithm;
  viewed: boolean;
  liked: boolean | null;
  created_at?: string;
  updated_at?: string;
  game?: Game; // Objeto completo do jogo, usado na interface e opcionalmente armazenado no banco
}

export interface AffiliateClick {
  id: string;
  user_id: string | null;
  store_product_id: string;
  recommendation_id: string | null;
  created_at: string;
  ip_address: string | null;
  user_agent: string | null;
  converted: boolean;
}

export interface GameSearchFilters {
  searchText?: string;
  categories?: string[];
  mechanics?: string[];
  themes?: string[];
  minPlayers?: number;
  maxPlayers?: number;
  minPlayTime?: number;
  maxPlayTime?: number;
  minComplexity?: number;
  maxComplexity?: number;
  year?: number;
  minAge?: number;
}

export interface RecommendationRequest {
  userId: string;
  preferenceWeight?: number; // Peso para preferências do usuário (0-1)
  collectionWeight?: number; // Peso para jogos da coleção (0-1)
  limit?: number; // Número de recomendações a retornar
  dislikedGameIds?: string[]; // IDs de jogos que receberam downvote e devem ser excluídos
  forcePersonalized?: boolean; // Forçar uso de algoritmos personalizados (não populares)
  filters?: RecommendationFilters; // Corrigindo o nome do tipo para RecommendationFilters
}

// Interfaces para o sistema de recomendação avançado
export interface UserInteraction {
  id: string;
  user_id: string;
  game_id: string;
  interaction_type: 'view' | 'rating' | 'wishlist' | 'collection';
  value?: number;
  created_at: string;
}

export interface UserSimilarity {
  id: string;
  user_id: string;
  target_user_id: string;
  similarity_score: number;
}

export interface SystemStats {
  id: number;
  total_ratings: number;
  total_interactions: number;
  model_trained: boolean;
  last_model_update: string | null;
}

export interface UserEmbedding {
  user_id: string;
  embedding: number[];
}

export interface GameEmbedding {
  game_id: string;
  embedding: number[];
}

// Enum para o tipo de algoritmo de recomendação
export enum RecommendationAlgorithm {
  POPULAR = 'popular',
  COLLABORATIVE = 'collaborative',
  CONTENT_BASED = 'content_based',
  HYBRID = 'hybrid',
  MACHINE_LEARNING = 'ml'
}

// Definição mais detalhada para filtros de recomendação
export interface RecommendationFilters {
  categories?: string[];
  minComplexity?: number;
  maxComplexity?: number;
  minPlayers?: number;
  maxPlayers?: number;
  maxPlayTime?: number;
}

// Interfaces para integração com a API da Ludopedia
export interface LudopediaCredentials {
  app_id: string;
  app_key: string;
  access_token: string | null;
}

export interface LudopediaTokenInfo {
  accessToken: string;
  refreshToken: string | null;
  expiresIn: number;
  tokenType: string;
}

export interface LudopediaUser {
  id: string;
  username: string;
  name: string;
  avatar_url: string | null;
  access_token: string;
}

export interface LudopediaGame {
  id: string;
  name: string;
  year: number;
  ano_nacional?: number | null;
  image_url: string;
  thumb_url: string;
  link?: string;
  min_players: number;
  max_players: number;
  min_playing_time: number;
  max_playing_time: number;
  min_age?: number;
  categories: string[];
  mechanics: string[];
  themes?: string[];
  artists?: string[];
  designers?: string[];
  bgg_id: number | null;
  complexity: number | null;
  stats?: {
    owned_count: number;
    owned_before_count: number;
    favorite_count: number;
    wishlist_count: number;
    played_count: number;
  };
}

export interface LudopediaCollection {
  games: LudopediaGame[];
  total: number;
  page: number;
  per_page: number;
}

// Definição do tipo para as conexões entre usuários
export interface UserConnection {
  id: string;
  user_id: string;
  connected_user_id: string;
  status: 'pending' | 'accepted' | 'rejected' | 'blocked';
  created_at: string;
  updated_at: string;
  // Campos opcionais para exibição na interface
  connected_profile?: UserProfile;
}

// Definição do tipo para mensagens entre usuários
export interface UserMessage {
  id: string;
  sender_id: string;
  recipient_id: string;
  content: string;
  read: boolean;
  created_at: string;
  updated_at: string;
  // Campos opcionais para exibição na interface
  sender_profile?: UserProfile;
  recipient_profile?: UserProfile;
}

// Definição do tipo para anúncios de mercado (compra/venda de jogos)
export interface MarketListing {
  id: string;
  user_id: string;
  game_id: string;
  listing_type: 'sell' | 'buy' | 'trade';
  price: number | null;
  condition?: 'new' | 'like_new' | 'good' | 'fair' | 'poor' | null;
  description: string | null;
  location: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  status: 'active' | 'completed' | 'canceled' | 'deleted';
  created_at: string;
  updated_at: string;
  user_profile?: UserProfile;
  game?: Game;
  include_sold?: boolean;
}

// Definição de filtros para busca de anúncios no mercado
export interface MarketFilters {
  listing_type?: 'sell' | 'buy' | 'trade';
  min_price?: number;
  max_price?: number;
  condition?: ('new' | 'like_new' | 'good' | 'fair' | 'poor')[];
  status?: 'active' | 'completed' | 'canceled' | 'deleted' | 'active_or_completed';
  search_text?: string;
  location?: string;
  categories?: string[];
  mechanics?: string[];
  min_players?: number;
  max_players?: number;
  min_playtime?: number;
  max_playtime?: number;
  min_age?: number;
  exclude_user_id?: string;
  user_id?: string;
  game_id?: string;
  city?: string;
  state?: string;
  country?: string;
  sort_by?: 'price' | 'created_at' | 'relevance';
  order?: 'asc' | 'desc';
  trade?: boolean;
  include_sold?: boolean;
}

export interface GameReview {
  id: string;
  user_id: string;
  game_id: string;
  review_text: string;
  created_at: string;
  updated_at: string;
  username?: string;
  avatar_url?: string;
  rating?: number;
  upvotes?: number;
  downvotes?: number;
  vote_balance?: number;
  user_vote?: 'upvote' | 'downvote' | null;
  game_name?: string;
  game_slug?: string;
  game_image_url?: string | null;
  game_thumb_url?: string | null;
  game_details?: {
    year?: number | null;
    min_players?: number | null;
    max_players?: number | null;
    // Outros campos do jogo podem ser adicionados aqui conforme necessário
  };
}